/**
 * The marks in issue, and the application's first picture.
 *
 * Until this card, every mark image three of the four registers publish was
 * fetched, magic-byte verified and stored — and then unreachable. `web/src`
 * contained zero `<img>` tags. An attorney on a figurative matter saw an empty
 * Mark field and a note explaining why it was empty, with the picture sitting
 * on the volume the whole time.
 *
 * Why the description and the observer render together
 * ------------------------------------------------------
 * They are one fact. `docs/DEVICE_MARKS.md` measured why a visual claim cannot
 * be checked the way every other claim in this application is — a ground code
 * is in the draft or it is not, a citation is in the retrieved set or it was
 * invented, but a device has no string and the corpus has none to lend
 * (`figurative`, `stylised`, `logo` and `get-up`: zero hits across all 23
 * corpus fixture files).
 *
 * So a description is worth exactly what its observer is worth, and this card
 * never shows one without the other. A model reading, when that path exists,
 * says so in the same line rather than looking like an attorney's.
 *
 * There is deliberately no similarity score anywhere on it. Nothing in the
 * corpus says how a hearing officer's scale distributes, and a number here
 * would be read as a finding.
 */
import { useState, type ChangeEvent, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import {
  observerLabel,
  signImageUrl,
  useMatterSigns,
  useSaveSigns,
  useDescribeSign,
  useUploadSignImage,
  type MatterSign,
  type MatterSignInput,
  type SignRole,
} from '@/api/signs'
import { useAuth } from '@/auth/AuthProvider'
import { Badge, Button, Card, CardHeader, Field, Input, Textarea, useToast } from '@/components/ui'

const ROLE_LABEL: Record<SignRole, string> = {
  SUBJECT: 'applied for',
  CITED: 'cited against it',
}

function toInput(sign: MatterSign): MatterSignInput {
  return {
    // Carried so the server can tell which sign each entry IS. The editor lets
    // an attorney change the ordinal, and without the id a swap would move one
    // mark's picture onto the other.
    id: sign.id,
    ordinal: sign.ordinal,
    role: sign.role,
    label: sign.label,
    word_element: sign.word_element,
    device_subject: sign.device_subject,
    dominant: sign.dominant,
    colours: sign.colours,
    goods: sign.goods,
  }
}

function blankSign(ordinal: number, role: SignRole): MatterSignInput {
  return { ordinal, role, label: '', word_element: null, device_subject: null, goods: null }
}

export function SignsCard({ matterId }: { matterId: string }) {
  const { hasScope } = useAuth()
  const canWrite = hasScope('matters:write')
  const signs = useMatterSigns(matterId)
  const [editing, setEditing] = useState(false)

  if (signs.isPending) return null

  const rows = signs.data ?? []
  // An error is NOT collapsed into "nothing recorded". A 500 and an empty set
  // are different answers and the screen says which one it got — the reviewer's
  // next move differs, and a card that showed "nothing here" over a failed
  // fetch would be telling them the marks were never recorded.
  if (signs.isError) {
    return (
      <Card role="group" aria-label="The marks being compared" className="gap-2">
        <CardHeader
          title="The marks being compared"
          description="These could not be loaded. They may well be recorded — it is this request that failed, not the record."
          actions={<Badge tone="warn">not loaded</Badge>}
        />
        <p className="m-0 text-micro text-muted">{errorMessage(signs.error)}</p>
      </Card>
    )
  }

  return (
    <Card role="group" aria-label="The marks being compared" className="gap-3">
      <CardHeader
        title="The marks being compared"
        description={
          rows.length === 0
            ? 'None recorded yet. On a logo objection these matter most — what the marks look like is the whole argument.'
            : 'What each mark is, and who described it. A description is only worth what its author is worth, so the two are always shown together.'
        }
        actions={
          canWrite && !editing ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {rows.length === 0 ? 'Record the marks' : 'Edit'}
            </Button>
          ) : null
        }
      />
      {editing ? (
        <SignsEditor matterId={matterId} rows={rows} onDone={() => setEditing(false)} />
      ) : rows.length === 0 ? (
        <p className="m-0 text-body text-muted">
          Nobody has recorded the marks on this matter yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((sign) => (
            <SignRow key={sign.ordinal} matterId={matterId} sign={sign} canWrite={canWrite} />
          ))}
        </div>
      )}
    </Card>
  )
}

function SignRow({
  matterId,
  sign,
  canWrite,
}: {
  matterId: string
  sign: MatterSign
  canWrite: boolean
}) {
  const toast = useToast()
  const upload = useUploadSignImage(matterId)
  const describe = useDescribeSign(matterId)
  // The first image in the application, so it is also the first broken one.
  // A mark whose bytes are missing from the volume must not render as the
  // browser's own broken-image glyph with no explanation.
  const [broken, setBroken] = useState(false)

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      await upload.mutateAsync({ ordinal: sign.ordinal, file })
      setBroken(false)
      toast.push('Picture attached', {
        tone: 'success',
        description: sign.device_subject
          ? 'The existing description now names a different picture — it is marked for re-checking.'
          : 'Describe what it shows to put it on the record.',
      })
    } catch (error) {
      toast.push('Could not attach the picture', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  async function onDescribe() {
    try {
      const answer = await describe.mutateAsync({ ordinal: sign.ordinal })
      // Not `answer.method`: that is `vlm_ocr:<model id>`, a code identifier.
      // The refreshed row carries the provenance line ("read by … — not an
      // attestation"), so the toast does not need to repeat it.
      toast.push('A model read the picture', {
        // Never 'success'. A machine reading is not a result to be pleased
        // about: it is a draft description nobody has confirmed, and the toast
        // says what has to happen next rather than congratulating anyone.
        tone: 'info',
        description: [
          'A proposal, not a description. Open the picture, correct what is wrong, and save it — a draft cannot argue from it until you do.',
          ...answer.notes,
        ].join(' '),
      })
    } catch (error) {
      toast.push('The model did not describe it', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-meta text-muted tabular-nums">{sign.ordinal}.</span>
        <span className="text-body">{sign.label}</span>
        <Badge tone={sign.role === 'SUBJECT' ? 'accent' : 'neutral'}>{ROLE_LABEL[sign.role]}</Badge>
        {sign.stale ? <Badge tone="warn">picture changed since</Badge> : null}
      </div>

      <div className="flex flex-wrap items-start gap-3">
        {sign.image_sha256 && !broken ? (
          <img
            src={signImageUrl(matterId, sign.image_sha256)}
            alt={`The mark: ${sign.label}`}
            className="max-h-24 max-w-[12rem] rounded-sm border border-divider bg-white object-contain"
            onError={() => setBroken(true)}
          />
        ) : (
          <p className="m-0 text-micro text-muted">
            {sign.image_sha256
              ? 'The picture is on this record but its file is not on the volume — the blob store may not be mounted.'
              : 'No picture on file for this sign.'}
          </p>
        )}
        <div className="flex min-w-[14rem] flex-1 flex-col gap-1">
          {sign.word_element ? (
            <p className="m-0 text-micro text-muted">Words in the sign: {sign.word_element}</p>
          ) : null}
          <p className="m-0 text-body">
            {sign.device_subject ?? (
              <span className="text-muted">
                Nobody has written down what this shows. On a figurative objection that is the
                fact the response turns on.
              </span>
            )}
          </p>
          {sign.dominant ? (
            <p className="m-0 text-micro text-muted">Dominant element: {sign.dominant}</p>
          ) : null}
          {sign.colours ? (
            <p className="m-0 text-micro text-muted">Colours: {sign.colours}</p>
          ) : null}
          <p className="m-0 text-micro text-muted">{observerLabel(sign)}</p>
          {sign.goods ? (
            <p className="m-0 text-micro text-muted">Goods and services: {sign.goods}</p>
          ) : null}
        </div>
      </div>

      {canWrite && sign.image_sha256 && sign.observed_by !== 'ATTORNEY' ? (
        // Offered only where there is something to read and nobody has read it
        // yet. Never over an attorney's own description: a machine reading does
        // not replace a person's, and the server refuses it too — this button
        // is the door, not the lock.
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            variant="secondary"
            disabled={describe.isPending}
            onClick={() => void onDescribe()}
          >
            {describe.isPending ? 'Reading the picture…' : 'Ask a model what this shows'}
          </Button>
          <p className="m-0 text-micro text-muted">
            A proposal for you to check, stamped as a machine reading. A response cannot argue
            from it until you have looked at the picture and saved it yourself.
          </p>
        </div>
      ) : null}

      {canWrite ? (
        <Field
          label={sign.image_sha256 ? 'Replace the picture' : 'Attach a picture'}
          hint="JPEG or PNG. Checked on the content, not the filename."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="file"
              accept="image/jpeg,image/png"
              disabled={upload.isPending}
              onChange={onFile}
            />
          )}
        </Field>
      ) : null}
    </div>
  )
}

function SignsEditor({
  matterId,
  rows,
  onDone,
}: {
  matterId: string
  rows: MatterSign[]
  onDone: () => void
}) {
  const toast = useToast()
  const save = useSaveSigns(matterId)
  const [draft, setDraft] = useState<MatterSignInput[]>(() =>
    rows.length > 0 ? rows.map(toInput) : [blankSign(1, 'SUBJECT')],
  )

  function update(ordinal: number, next: Partial<MatterSignInput>) {
    setDraft((current) =>
      current.map((row) => (row.ordinal === ordinal ? { ...row, ...next } : row)),
    )
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    const cleaned = draft.filter((row) => row.label.trim().length > 0)
    try {
      await save.mutateAsync(cleaned)
      toast.push('Marks recorded', {
        tone: 'success',
        description: cleaned.some((row) => row.device_subject?.trim())
          ? 'Your description is on the record under your name.'
          : 'Recorded. A description is attributed to whoever writes it.',
      })
      onDone()
    } catch (error) {
      toast.push('Could not record the marks', {
        tone: 'error',
        description: errorMessage(error),
      })
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={onSubmit}>
      {draft.map((row) => (
        <div key={row.ordinal} className="flex flex-col gap-3 rounded-md border border-divider p-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-meta text-muted tabular-nums">{row.ordinal}.</span>
            <Badge tone={row.role === 'SUBJECT' ? 'accent' : 'neutral'}>
              {ROLE_LABEL[row.role]}
            </Badge>
          </div>
          <Field label="What to call this mark" hint="Required — how it will be referred to in the response.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={row.label}
                onChange={(event) => update(row.ordinal, { label: event.target.value })}
                placeholder="the applied-for device"
              />
            )}
          </Field>
          <Field label="Any words in the mark" hint="Leave empty for a logo with no words — that is a fact about the mark, not a missing answer.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={row.word_element ?? ''}
                onChange={(event) =>
                  update(row.ordinal, { word_element: event.target.value || null })
                }
              />
            )}
          </Field>
          <Field
            label="What the picture shows"
            hint="This goes on the record under your name. Describe what you can see, not what you conclude from it."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={2}
                value={row.device_subject ?? ''}
                onChange={(event) =>
                  update(row.ordinal, { device_subject: event.target.value || null })
                }
                placeholder="A stylised bird in outline, facing left, above the word element."
              />
            )}
          </Field>
          <Field label="Goods and services" hint="Copy these as written on the register.">
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={2}
                value={row.goods ?? ''}
                onChange={(event) => update(row.ordinal, { goods: event.target.value || null })}
              />
            )}
          </Field>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? 'Recording…' : 'Record the marks'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={save.isPending}
          onClick={() =>
            setDraft((current) => [
              ...current,
              blankSign(Math.max(0, ...current.map((row) => row.ordinal)) + 1, 'CITED'),
            ])
          }
        >
          Add a cited mark
        </Button>
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
